import { Cloud, Loader2, Save, AlertTriangle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { PosSettings } from '@/types';
import type { PosSyncMode } from '@/offline/db';
import { useSyncStore } from '@/store/syncStore';
import { isDesktopApp } from '@/desktop/electronApi';
import OfflineSyncPanel from '@/components/offline/OfflineSyncPanel';
import DesktopUnlockSettings from '@/components/desktop/DesktopUnlockSettings';
import PwaInstallCard from '@/components/common/PwaInstallCard';
import { resolveFileUrl } from '@/utils/fileUrl';

interface Props {
  posForm: PosSettings;
  setPosForm: (next: PosSettings) => void;
  setPosError: (msg: string) => void;
  onSave: (e: React.FormEvent) => void;
  isSaving: boolean;
  message: string;
  error: string;
  companyName?: string;
  companyLogo?: string;
}

export default function PosSyncModeTab({
  posForm,
  setPosForm,
  setPosError,
  onSave,
  isSaving,
  message,
  error,
  companyName,
  companyLogo,
}: Props) {
  const { t } = useTranslation();
  const desktop = isDesktopApp();
  const mode = posForm.pos_sync_mode ?? 'Live Online';
  const isLocalMode =
    mode === 'Local First Auto Sync' || mode === 'Offline Always Manual Sync';
  const logoUrl = companyLogo ? resolveFileUrl(companyLogo) : '';

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Cloud className="w-5 h-5 text-primary-600" />
          {t('settings.posSyncTitle')}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {desktop ? t('settings.posSyncDescDesktop') : t('settings.posSyncDesc')}
        </p>
      </div>

      <form onSubmit={onSave} className="space-y-5">
        <div>
          <p className="font-medium text-gray-900 text-sm mb-1">{t('settings.posSyncMode')}</p>
          <p className="text-xs text-gray-500 mb-2">{t('settings.posSyncModeHelp')}</p>
          <select
            value={mode}
            onChange={(e) => {
              const next = e.target.value as PosSyncMode;
              const { pendingQueueCount, failedQueueCount } = useSyncStore.getState();
              const blocked = pendingQueueCount + failedQueueCount;
              if (next === 'Live Online' && blocked > 0) {
                const ok = window.confirm(
                  t('settings.posSyncQueueConfirm', {
                    count: blocked,
                    pending: pendingQueueCount,
                    failed: failedQueueCount,
                  }),
                );
                if (!ok) {
                  setPosError(
                    t('settings.posSyncQueueBlocked', { count: blocked }),
                  );
                  return;
                }
                void import('@/offline').then(async ({ discardAllQueueItems }) => {
                  await discardAllQueueItems();
                  setPosForm({ ...posForm, pos_sync_mode: next });
                  setPosError('');
                });
                return;
              }
              setPosForm({ ...posForm, pos_sync_mode: next });
              setPosError('');
            }}
            className="input-field w-full max-w-md"
          >
            <option value="Live Online">{t('settings.posSyncLiveOnline')}</option>
            <option value="Local First Auto Sync">{t('settings.posSyncLocalFirst')}</option>
            <option value="Offline Always Manual Sync">{t('settings.posSyncOfflineAlways')}</option>
          </select>
        </div>

        {isLocalMode && (
          <div className="space-y-4 pt-2 border-t border-gray-100">
            <DesktopUnlockSettings />
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="font-medium text-gray-900 text-sm">{t('settings.allowNegativeStockSync')}</p>
                <p className="text-xs text-gray-500">{t('settings.allowNegativeStockSyncHelp')}</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setPosForm({
                    ...posForm,
                    offline_sync_allow_negative_stock: !posForm.offline_sync_allow_negative_stock,
                  })
                }
                className={`px-4 py-1.5 text-xs font-semibold rounded-full border ${
                  posForm.offline_sync_allow_negative_stock
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}
              >
                {posForm.offline_sync_allow_negative_stock ? t('common.enabled') : t('common.disabled')}
              </button>
            </div>
            <div className="flex items-center justify-between py-1">
              <div>
                <p className="font-medium text-gray-900 text-sm">{t('settings.bypassShiftOffline')}</p>
                <p className="text-xs text-gray-500">{t('settings.bypassShiftOfflineHelp')}</p>
              </div>
              <button
                type="button"
                onClick={() =>
                  setPosForm({
                    ...posForm,
                    offline_bypass_shift_in_local_modes: !posForm.offline_bypass_shift_in_local_modes,
                  })
                }
                className={`px-4 py-1.5 text-xs font-semibold rounded-full border ${
                  posForm.offline_bypass_shift_in_local_modes
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-gray-50 text-gray-500 border-gray-200'
                }`}
              >
                {posForm.offline_bypass_shift_in_local_modes ? t('common.enabled') : t('common.disabled')}
              </button>
            </div>
            <div className="flex items-center justify-between py-1 gap-4">
              <div>
                <p className="font-medium text-gray-900 text-sm">{t('settings.offlineInvoicePrefix')}</p>
                <p className="text-xs text-gray-500">{t('settings.offlineInvoicePrefixHelp')}</p>
              </div>
              <input
                className="input-field w-28"
                value={posForm.offline_invoice_prefix ?? 'OFF'}
                onChange={(e) =>
                  setPosForm({ ...posForm, offline_invoice_prefix: e.target.value.slice(0, 20) })
                }
              />
            </div>
            <OfflineSyncPanel />
          </div>
        )}

        {message && (
          <div className="flex items-center gap-2 text-emerald-700 text-sm">
            <CheckCircle className="w-4 h-4" /> {message}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}
        <button type="submit" disabled={isSaving} className="btn-primary flex items-center gap-2">
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {t('settings.saveSyncSettings')}
        </button>
      </form>

      {!desktop && (
        <PwaInstallCard
          appName={companyName}
          shortName={companyName?.split(/\s+/)[0]}
          logoUrl={logoUrl || undefined}
        />
      )}
    </div>
  );
}
